import { createRouter, createWebHistory } from "vue-router";
import axios from "axios";
import LoginPage from "@/components/pages/global/LoginPage.vue";
import MainPage from "@/components/pages/global/MainPage.vue";
import { canAccessPath } from "@/utils/roleAccess";
import { normalizePdsApprovalStatus } from "@/utils/pdsStatus";

const routes = [
  {
    path: "/",
    name: "LoginPage",
    component: LoginPage,
  },
  {
    path: "/dashboard",
    name: "MainPage",
    component: MainPage,
    children: [
      // TODO ADMIN ROUTES
      {
        path: "/dashboard",
        name: "Dashboard",
        component: () => import("@/components/pages/global/DashboardPage.vue"),
      },
      {
        path: "/manage-employee",
        name: "ManageEmployees",
        component: () => import("@/components/pages/admin/EmployeePage.vue"),
      },
      {
        path: "/attendance",
        name: "Attendance",
        component: () => import("@/components/pages/admin/AttendancePage.vue"),
      },
      {
        path: "/client-module",
        name: "ClientModule",
        component: () => import("@/components/pages/admin/ClientPage.vue"),
      },
      {
        path: "/onboard-module",
        name: "OnboardModule",
        component: () => import("@/components/pages/admin/OnboardPage.vue"),
      },
      {
        path: "/hr-requests",
        name: "HrRequests",
        component: () =>
          import("@/components/pages/global/HrRequestsPage.vue"),
      },
      /** Same UI as /hr-requests; separate path so admin sidebar (Service Providers → Requests) does not share active state with My Account → HR Requests. */
      {
        path: "/manage-hr-requests",
        name: "ManageHrRequests",
        component: () =>
          import("@/components/pages/global/HrRequestsPage.vue"),
      },
      {
        path: "/view-onboard",
        name: "ViewOnboardModule",
        component: () =>
          import("@/components/pages/admin/modals/view/ViewOnboard.vue"),
      },
      {
        path: "/view-scheduled",
        name: "ViewScheduledPage",
        component: () => import("@/components/pages/admin/ScheduledPage.vue"),
      },
      {
        path: "/view-employee-details/:id",
        name: "EmployeeDetails",
        component: () =>
          import(
            "@/components/pages/admin/modals/view/ViewEmployeeDetails.vue"
          ),
      },
      {
        path: "/user-account-module",
        name: "UserAccountPage",
        component: () => import("@/components/pages/admin/UserAccountPage.vue"),
      },

      {
        path: "/settings",
        component: () =>
          import("@/components/pages/admin/settings/SettingsLayout.vue"),
        redirect: "/settings/account",
        children: [
          {
            path: "account",
            name: "SettingsAccount",
            component: () =>
              import("@/components/pages/admin/settings/AccountProfilePage.vue"),
          },
          {
            path: "roles",
            name: "SettingsRoles",
            component: () =>
              import(
                "@/components/pages/admin/settings/RolesPermissionsPage.vue"
              ),
          },
          {
            path: "departments",
            name: "SettingsDepartments",
            component: () =>
              import(
                "@/components/pages/admin/settings/SettingsDepartmentsPage.vue"
              ),
          },
          {
            path: "users",
            name: "SettingsUsers",
            component: () =>
              import(
                "@/components/pages/admin/settings/SettingsUserManagementPage.vue"
              ),
          },
          {
            path: "logs",
            name: "SettingsLogs",
            component: () =>
              import("@/components/pages/admin/settings/SettingsPlaceholder.vue"),
            meta: {
              title: "System Logs",
              description:
                "System activity logging is not configured yet. Check back later.",
            },
          },
        ],
      },
      {
        path: "/settings/profile",
        redirect: "/settings/account",
      },

      // TODO PROVIDER ROUTES (PDS / self-service)
      {
        path: "/view-my-details",
        name: "MyDetails",
        component: () => import("@/components/pages/employee/MyPDS.vue"),
      },
      {
        path: "/skill-profiles",
        redirect: { path: "/view-my-details", query: { tab: "skills" } },
      },
      {
        path: "/employee-registration",
        name: "EmployeeRegistration",
        component: () =>
          import(
            "@/components/pages/employee/registration/RegistrationForm.vue"
          ),
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async (to, _from, next) => {
  if (to.path === "/" || to.name === "LoginPage") {
    next();
    return;
  }
  try {
    const { data } = await axios.get(
      `${process.env.VUE_APP_API_BASE_URL}/auth/me`,
      { withCredentials: true },
    );
    const code = String(data?.role_code || "").trim().toUpperCase();
    if (!canAccessPath(to.path, code)) {
      next({ path: "/dashboard" });
      return;
    }
    if (code === "PROVIDER" && to.path === "/hr-requests") {
      try {
        const uid = data?.user_id;
        if (uid == null) {
          next({ path: "/view-my-details" });
          return;
        }
        const empRes = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/employee-details/user/${uid}`,
          { withCredentials: true },
        );
        const emp = Array.isArray(empRes.data) ? empRes.data[0] : empRes.data;
        if (normalizePdsApprovalStatus(emp?.pds_approval_status) !== "Approved") {
          next({ path: "/view-my-details" });
          return;
        }
      } catch {
        next({ path: "/view-my-details" });
        return;
      }
    }
    next();
  } catch {
    next({ path: "/" });
  }
});

export default router;
